<template>
	<div class="app-container no-padding no-scroll" :class="{ content__admin: isEditor }">

		<ArticleAdminRibbon
			:article="article"
			:loading="loading"
			@pingsaveineditor="pingSaveInEditor"
			@togglepublish="togglePublish"
		/>

		<div class="layout columns">
			
			<div class="column localnav">

				<SideNavigation
					v-if="!error"
					:articleJson="article.json"
					:title="article.title"
					:articleId="article.id"
					:itemInView="itemInView"
				/>
			</div>

			<div class="column article">

				<ArticleBreadCrumbs :article="article" />

				<Editor
					v-if="!error"
					@requestsave="saveArticle"
					@reqautosave="autoSaveArticle"
					@itemscrolledinview="onItemInView"
					:isAdmin="isEditor"
					:article="article"
					:editable="true"
					ref="editor"
				/>

				<Spinner v-if="loadingUser" />

				<ErrorAlert class="mt-5" v-if="error" :error="error" />
			</div>

			<div class="column info">

				<SidePanel
					v-if="!error"
					:relatedArticles="article.articlesMinimal"
					:articleId="article.id"
					ref="sidePanel"
				/>

			</div>

		</div>

		<ErrorDialog ref="errDialog" />
	</div>
</template>

<script>
import SideNavigation from './SideNavigation';
import Editor from './Editor';
import Article from '../../../services/article';
import SidePanel from './SidePanel';
import Spinner from '../../shared/Spinner';
import ErrorAlert from '../../shared/ErrorAlert';
import ErrorDialog from '../../shared/ErrorDialog';
import ArticleBreadCrumbs from './ArticleBreadCrumbs';
import LinkHighlight from '../../../util/classes/LinkHighlight';
import ArticleAdminRibbon from './ArticleAdminRibbon';
import { mapGetters } from 'vuex';
import { v4 as uuidv4 } from 'uuid';

export default {
	name: 'ArticleHome',
	components: {
		SideNavigation,
		Editor,
		SidePanel,
		Spinner,
		ErrorAlert,
		ErrorDialog,
		ArticleBreadCrumbs,
		ArticleAdminRibbon,
	},
	data() {
		return {
			article: { html: '' },
			parentArticles: [],
			loading: false,
			loadingUser: false,
			itemInView: '',
			error: null,
		};
	},
	mounted() {
		this.loadArticle();
	},
	methods: {
		pingSaveInEditor: function () {
			this.$refs.editor.autoSaveArticle();
		},
		autoSaveArticle: function (article) {
			let payload = this.assembleSavePayload(article);
			if (!payload.title || payload.title.length == 0) {
				this.$refs.errDialog.show({
					message: 'Article must have a title!',
				});
				return;
			}
			this.executeArticleAutoSave(payload);
		},
		assembleSavePayload: function (article) {
			article.parentArticleId = this.article.parentArticleId;
			article.articleType = this.article.articleType;
			article.published = this.article.published;
			article.html = LinkHighlight.clearLinks(article.html);
			return article;
		},
		executeArticleAutoSave: function (payload) {
			this.loading = true;
			Article.instance()
				.editArticle(payload)
				.then((response) => {
					this.loading = false;
					this.article.id = response.data.id;
					this.article.json = response.data.json;
					this.article.title = response.data.title;
					this.article.articlesMinimal =
						response.data.articlesMinimal;
					this.$store.dispatch('fetchIndexArticles');
					if (this.isEditor) this.$refs.sidePanel.getArticleHistory();
				})
				.catch((error) => {
					this.loading = false;
					this.$refs.errDialog.show(error);
					this.$refs.editor.stopAutosave();
				});
		},
		saveArticle: function (article) {
			let payload = this.assembleSavePayload(article);
			if (!payload.title || payload.title.length == 0) {
				this.$refs.errDialog.show({
					message: 'Article must have a title!',
				});
				return;
			}
			this.executeArticleSaveReq(payload);
		},
		executeArticleSaveReq: function (payload) {
			this.loading = true;
			Article.instance()
				.editArticle(payload)
				.then((response) => {
					this.loading = false;
					this.handleSaveResponse(response);
				})
				.catch((error) => {
					this.$refs.errDialog.show(error);
					this.loading = false;
					this.$refs.editor.stopAutosave();
				});
		},
		handleSaveResponse: function (res) {
			let id = res.data.id;
			this.$store.dispatch('fetchIndexArticles');

			this.$router.push({
				path: '/article-view',
				query: {
					id,
					pos: this.$refs.editor.$el.scrollTop,
				},
			});
		},
		loadArticle: function () {
			let articleId = this.$route.query.id;
			let articleType = this.$route.query.type;

			if (this.article && articleId && articleId == this.article.id) {
				return;
			}

			if (articleId) {
				this.getArticle(articleId);
			} else {
				this.article = {
					html: `<h2 id=${uuidv4()}>Synopsis</h2><p></p>`,
					title: 'This is a new article.',
					articleType: articleType || null,
					parentArticleId: null,
					aliases: [],
				};
				this.autoSaveArticle(this.article);
			}
		},
		getArticle: function (id) {
			this.loadingUser = true;
			Article.instance()
				.getArticleById(id)
				.then((res) => {
					this.removeError();
					this.article = res.data;
					this.highlightLinks();
					this.loadingUser = false;
				})
				.catch((error) => {
					this.addError(error);
					this.loadingUser = false;
				});
		},
		togglePublish: function () {
			this.article.published = !this.article.published;
			this.pingSaveInEditor();
		},
		highlightLinks: function () {
			let linkHighlighter = new LinkHighlight(
				this.article.html,
				this.article.articlesMinimal,
				this.article.id
			);
			this.article.html = linkHighlighter.getHighlightHtml();
		},
		onItemInView: function (id) {
			this.itemInView = id;
		},
		addError: function (error) {
			this.error = error;
		},
		removeError: function () {
			this.error = null;
		},
	},
	computed: {
		...mapGetters(['isEditor']),
	},
	watch: {
		$route: 'loadArticle',
	},
};
</script>
